/**
 * MKS-347 — the Leads board's search and filters.
 *
 * Everything here is pure: parse the state out of the URL, serialize it back,
 * and apply it to a lead array. `GET /api/leads` already returns the whole
 * board in one request, so filtering is a client-side narrowing of what is
 * already in hand — no new query parameters, no second round-trip.
 *
 * The URL is the storage, like the ticket board (`utils/boardFilterUrl.ts`):
 * a filtered view is a link somebody can paste into Slack.
 */
import type { Lead } from '../services/api/leads';

/** The expected-close windows the ticket asks for. */
export const CLOSE_WINDOWS = ['this_month', 'this_quarter', 'overdue'] as const;
export type CloseWindow = (typeof CLOSE_WINDOWS)[number];

export const CLOSE_WINDOW_LABELS: Record<CloseWindow, string> = {
  this_month: 'This month',
  this_quarter: 'This quarter',
  overdue: 'Overdue',
};

export interface LeadBoardFilters {
  /** Free text over lead name, company and contact. */
  search: string;
  /** Owner ids; empty means every owner. OR within the slot. */
  ownerIds: number[];
  /** Source ids; `null` inside the array means "no source". */
  sourceIds: (number | null)[];
  /** Stage ids; empty means every column. */
  stageIds: number[];
  /** Label ids; a lead matches when it carries at least one. */
  labelIds: number[];
  /** Expected-close window, or `null` for "any time". */
  closeWindow: CloseWindow | null;
  /** The "My deals" toggle: owner = the current user. */
  mineOnly: boolean;
}

export const EMPTY_LEAD_BOARD_FILTERS: LeadBoardFilters = {
  search: '',
  ownerIds: [],
  sourceIds: [],
  stageIds: [],
  labelIds: [],
  closeWindow: null,
  mineOnly: false,
};

/**
 * Every URL key this module owns. The page clears all of them before writing
 * the current state back, so a slot that just became inactive never leaves a
 * stale value behind (same contract as `BOARD_FILTER_URL_PARAM_KEYS`).
 */
export const LEAD_FILTER_URL_PARAM_KEYS = [
  'q',
  'owner',
  'source',
  'stage',
  'label',
  'close',
  'mine',
] as const;

/** `null` is spelled `none` in the URL: `?source=none` is "leads with no source". */
const NO_VALUE = 'none';

function parseIdList(raw: string | null): number[] {
  if (!raw) return [];
  const out: number[] = [];
  for (const part of raw.split(',')) {
    const n = Number(part);
    if (Number.isInteger(n) && n > 0) out.push(n);
  }
  return out;
}

function parseNullableIdList(raw: string | null): (number | null)[] {
  if (!raw) return [];
  const out: (number | null)[] = [];
  for (const part of raw.split(',')) {
    if (part === NO_VALUE) {
      out.push(null);
      continue;
    }
    const n = Number(part);
    if (Number.isInteger(n) && n > 0) out.push(n);
  }
  return out;
}

export function parseLeadFiltersFromUrl(params: URLSearchParams): LeadBoardFilters {
  const close = params.get('close');
  return {
    search: params.get('q') ?? '',
    ownerIds: parseIdList(params.get('owner')),
    sourceIds: parseNullableIdList(params.get('source')),
    stageIds: parseIdList(params.get('stage')),
    labelIds: parseIdList(params.get('label')),
    closeWindow: CLOSE_WINDOWS.includes(close as CloseWindow) ? (close as CloseWindow) : null,
    mineOnly: params.get('mine') === '1',
  };
}

/** Only the ACTIVE slots are emitted, so an unfiltered board has a clean URL. */
export function serializeLeadFiltersToParams(filters: LeadBoardFilters): Record<string, string> {
  const out: Record<string, string> = {};
  if (filters.search.trim()) out.q = filters.search;
  if (filters.ownerIds.length) out.owner = filters.ownerIds.join(',');
  if (filters.sourceIds.length) {
    out.source = filters.sourceIds.map((id) => (id === null ? NO_VALUE : String(id))).join(',');
  }
  if (filters.stageIds.length) out.stage = filters.stageIds.join(',');
  if (filters.labelIds.length) out.label = filters.labelIds.join(',');
  if (filters.closeWindow) out.close = filters.closeWindow;
  if (filters.mineOnly) out.mine = '1';
  return out;
}

export function hasActiveLeadFilters(filters: LeadBoardFilters): boolean {
  return Object.keys(serializeLeadFiltersToParams(filters)).length > 0;
}

/** The haystack a search term is matched against: lead, company, contacts. */
function searchHaystack(lead: Lead): string {
  const parts: (string | null | undefined)[] = [
    lead.name,
    lead.customer?.name,
    // MKS-344's reusable contacts and the three legacy free-text fields it
    // supersedes: both are searched, so the box works either side of that
    // ticket's own release flag.
    lead.contactPerson,
    lead.contactEmail,
    ...(lead.contacts ?? []).flatMap((c) => [c.name, c.email]),
  ];
  return parts.filter(Boolean).join(' ').toLowerCase();
}

/**
 * Is `date` (a `YYYY-MM-DD` string) inside the window, measured from `today`?
 *
 * "Overdue" is strictly before today AND on a deal that is still open — a deal
 * that closed last quarter is not an overdue one. That check needs the lead,
 * so it lives in `matchesLead` below; this only answers the date question.
 */
function matchesCloseWindow(
  expectedCloseDate: string | null,
  window: CloseWindow,
  today: Date,
): boolean {
  if (!expectedCloseDate) return false;
  // Parsed as UTC midnight, like every other date-only field in the SPA, so a
  // timezone west of UTC cannot shift a close date into the previous day.
  const due = new Date(`${expectedCloseDate}T00:00:00Z`);
  if (Number.isNaN(due.getTime())) return false;

  const year = today.getUTCFullYear();
  const month = today.getUTCMonth();
  const startOfToday = Date.UTC(year, month, today.getUTCDate());

  if (window === 'overdue') return due.getTime() < startOfToday;
  if (window === 'this_month') {
    return due.getUTCFullYear() === year && due.getUTCMonth() === month;
  }
  // this_quarter
  const quarter = Math.floor(month / 3);
  return (
    due.getUTCFullYear() === year && Math.floor(due.getUTCMonth() / 3) === quarter
  );
}

export interface LeadFilterContext {
  /** The signed-in user, for the "My deals" toggle. `null` disables it. */
  currentUserId: number | null;
  /** Injected so the window tests are not clock-dependent. */
  today?: Date;
}

function matchesLead(lead: Lead, filters: LeadBoardFilters, ctx: LeadFilterContext): boolean {
  const term = filters.search.trim().toLowerCase();
  if (term && !searchHaystack(lead).includes(term)) return false;

  if (filters.mineOnly) {
    // Fail closed: with no known user, "My deals" matches nothing rather than
    // silently showing everybody's.
    if (ctx.currentUserId === null || lead.owner?.id !== ctx.currentUserId) return false;
  }

  if (filters.ownerIds.length && !filters.ownerIds.includes(lead.owner?.id)) return false;

  if (filters.sourceIds.length && !filters.sourceIds.includes(lead.sourceId ?? null)) {
    return false;
  }

  if (filters.stageIds.length && !filters.stageIds.includes(lead.stageId)) return false;

  // OR semantics, the same as the label chips this replaces: a lead matches
  // if it carries AT LEAST ONE of the selected labels.
  if (filters.labelIds.length && !lead.labels.some((l) => filters.labelIds.includes(l.id))) {
    return false;
  }

  if (filters.closeWindow) {
    const today = ctx.today ?? new Date();
    if (!matchesCloseWindow(lead.expectedCloseDate, filters.closeWindow, today)) return false;
    // An "overdue" deal that is already won or lost is history, not a problem.
    if (filters.closeWindow === 'overdue' && lead.stageKind !== 'open') return false;
  }

  return true;
}

/** AND across slots, OR within each slot. Returns `leads` itself when idle. */
export function filterLeads(
  leads: Lead[],
  filters: LeadBoardFilters,
  ctx: LeadFilterContext,
): Lead[] {
  if (!hasActiveLeadFilters(filters)) return leads;
  return leads.filter((lead) => matchesLead(lead, filters, ctx));
}
